/**
 * Journey order for the reveal card. "Whose steps?" clues run hardest-first, so drawing
 * them in authored order zig-zags; the reveal sorts them by the date in `when` instead.
 */
import type { Place } from "../puzzles/types";

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
/** A season as a month, for ordering only (northern-hemisphere sources). */
const SEASON_MONTH: Record<string, number> = { spring: 4, summer: 7, autumn: 10, fall: 10 };

/**
 * Sortable key for a free-text period: year × 10,000 + month × 100 + day (+ hour / 100), from the first
 * 3–4 digit year ("2018–2021", "c. 1450", "late 1880s", "from 1901", "10 Apr 1912").
 * BC/BCE years count as negative. null when there is no year.
 */
export function whenKey(when: string | undefined | null): number | null {
  const w = whenParts(when);
  return w ? w.year * 10000 + w.month * 100 + w.day + timeOfDay(when!) / 100 : null;
}

/** Year (BC negative), month (1–12, 0 = none) and day (0 = none) of a free-text period; null when it has no year. */
export function whenParts(when: string | undefined | null): { year: number; month: number; day: number } | null {
  if (!when) return null;
  const y = /(?<!\d)(\d{3,4})(?!\d)/.exec(when);
  if (!y) return null;
  // "BC" anywhere makes the year BC, so a range like "1181–1174 BC" reads as 1181 BC.
  const year = Number(y[1]) * (/\d\s*s?\s*(BCE|BC|B\.C\.)/i.test(when) ? -1 : 1);
  const m = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?/i.exec(when);
  // Sources often give only a season ("summer 999", "autumn 999"): enough to order two stops in one year.
  const season = m ? null : /\b(spring|summer|autumn|fall)\b/i.exec(when);
  const month = m ? MONTHS.indexOf(m[1]!.toLowerCase()) + 1 : season ? SEASON_MONTH[season[1]!.toLowerCase()]! : 0;
  const d = m ? /(?<!\d)(\d{1,2})(?!\d)\s+(?=jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.exec(when) : null;
  return { year, month, day: d ? Number(d[1]) : 0 };
}

/** Hour of day (0–23.99) from "noon", "midnight" or a clock time like "4 pm", "4–8 pm", "18:35"; 0 if none. For same-day ties. */
export function timeOfDay(when: string): number {
  if (/\bnoon\b/i.test(when)) return 12;
  if (/\bmidnight\b/i.test(when)) return 0;
  const ampm = /(?<![\d:])(\d{1,2})(?::(\d{2}))?(?:\s*[–-]\s*\d{1,2}(?::\d{2})?)?\s*(am|pm)\b/i.exec(when);
  if (ampm) return ((Number(ampm[1]) % 12) + (ampm[3]!.toLowerCase() === "pm" ? 12 : 0)) + Number(ampm[2] ?? 0) / 60;
  const hm = /(?<!\d)([01]?\d|2[0-3]):([0-5]\d)(?!\d)/.exec(when);
  return hm ? Number(hm[1]) + Number(hm[2]) / 60 : 0;
}

/**
 * Places in journey order. `dated` is false when any place has no parsable year: then the
 * authored order is kept and the reveal draws numbered pins without connecting lines.
 * Ties keep their authored order (stable sort).
 */
export function journeyOrder<T extends Pick<Place, "when">>(places: readonly T[]): { stops: T[]; dated: boolean } {
  const keys = places.map((p) => whenKey(p.when));
  if (keys.some((k) => k == null)) return { stops: [...places], dated: false };
  const idx = places.map((_, i) => i).sort((a, b) => keys[a]! - keys[b]! || a - b);
  return { stops: idx.map((i) => places[i]!), dated: true };
}
